import React, { useState } from 'react';
import { Box, Typography, Button, Modal, Fade, LinearProgress, IconButton, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../contexts/AuthContext';

// Tutorial step icons
const GatewayIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2">
    <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
  </svg>
);

const WorkspaceIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2">
    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
    <polyline points="9,22 9,12 15,12 15,22"/>
  </svg>
);

const CloudIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2">
    <path d="M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z"/>
  </svg>
);

const RepoIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2">
    <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
  </svg>
);

const CodeIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2">
    <polyline points="16,18 22,12 16,6"/>
    <polyline points="8,6 2,12 8,18"/>
  </svg>
);

const AIIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2">
    <path d="M12 2L2 7l10 5 10-5-10-5z"/>
    <path d="M2 17l10 5 10-5"/>
    <path d="M2 12l10 5 10-5"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
    <path d="M20 6L9 17l-5-5"/>
  </svg>
);

interface TutorialStep {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  hasWorkspaceInput?: boolean;
  codeSnippet?: string;
}

const tutorialSteps: TutorialStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to Cloud & AI Gateway',
    description: 'The Gateway is your central hub for managing cloud storage resources and AI integrations. Let\'s get you started with the basics.',
    icon: <GatewayIcon />,
  },
  {
    id: 'workspace',
    title: 'Create Your First Workspace',
    description: 'Workspaces help you organize your resources. Create your first workspace to start managing your cloud storage and AI services.',
    icon: <WorkspaceIcon />,
    hasWorkspaceInput: true,
  },
  {
    id: 'cloud',
    title: 'Connect Cloud Storage',
    description: 'Connect your cloud storage providers like AWS S3, Google Cloud Storage, or Azure Blob. Your data stays secure with encrypted connections.',
    icon: <CloudIcon />,
  },
  {
    id: 'repo',
    title: 'Create a Repository',
    description: 'Repositories group your cloud resources together. Create a repository to organize your connected storage buckets and manage access.',
    icon: <RepoIcon />,
  },
  {
    id: 'ai',
    title: 'AI Integration',
    description: 'Connect AI providers like OpenAI, Anthropic, or Google Gemini. Use our unified API to interact with multiple AI models seamlessly.',
    icon: <AIIcon />,
  },
  {
    id: 'code',
    title: 'Start Building',
    description: 'Use our SDK to interact with your resources. Here\'s a quick example to get you started:',
    icon: <CodeIcon />,
    codeSnippet: `import { FlashbackClient } from '@flashback/sdk';

const client = new FlashbackClient({
  apiKey: 'your-api-key'
});

// Query your data with AI
const response = await client.chat({
  model: 'gpt-4',
  messages: [
    { role: 'user', content: 'Analyze my data' }
  ],
  repository: 'my-repo'
});`,
  },
  {
    id: 'complete',
    title: 'You\'re Ready!',
    description: 'Great job! You\'re all set to use the Cloud & AI Gateway. Explore the dashboard to manage your resources and start building.',
    icon: <CheckIcon />,
  },
];

interface GatewayTutorialProps {
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
  onCreateWorkspace: (name: string) => void;
  onNavigate: (section: 'cloud-storage' | 'repositories' | 'ai') => void;
}

const GatewayTutorial: React.FC<GatewayTutorialProps> = ({
  open,
  onClose,
  onComplete,
  onCreateWorkspace,
  onNavigate,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [workspaceName, setWorkspaceName] = useState('My Workspace');
  const { globalData } = useAuth();
  const firstName = globalData?.userProfile?.firstName || 'there';

  const step = tutorialSteps[currentStep];
  const progress = ((currentStep + 1) / tutorialSteps.length) * 100;
  const isLastStep = currentStep === tutorialSteps.length - 1;
  const isFirstStep = currentStep === 0;

  const handleNext = () => {
    // If on workspace step and has input, create workspace
    if (step.id === 'workspace' && workspaceName.trim()) {
      onCreateWorkspace(workspaceName.trim());
    }
    
    if (isLastStep) {
      onComplete();
      try {
        localStorage.setItem('gatewayTutorialCompleted', 'true');
      } catch { void 0; }
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    onClose();
    try {
      localStorage.setItem('gatewayTutorialCompleted', 'true');
    } catch { void 0; }
  };

  // Get step-specific gradient
  const getStepGradient = () => {
    switch (step.id) {
      case 'workspace': return 'linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)';
      case 'cloud': return 'linear-gradient(135deg, #06B6D4 0%, #22D3EE 100%)';
      case 'repo': return 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)';
      case 'ai': return 'linear-gradient(135deg, #10B981 0%, #34D399 100%)';
      case 'code': return 'linear-gradient(135deg, #3B82F6 0%, #60A5FA 100%)';
      case 'complete': return 'linear-gradient(135deg, #10B981 0%, #34D399 100%)';
      default: return 'linear-gradient(135deg, #617BFF 0%, #8B5CF6 100%)';
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleSkip}
      closeAfterTransition
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Fade in={open}>
        <Box
          sx={{
            bgcolor: 'white',
            borderRadius: '20px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            maxWidth: 560,
            width: '90%',
            outline: 'none',
            overflow: 'hidden',
          }}
        >
          {/* Progress bar */}
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 4,
              bgcolor: '#E5E7EB',
              '& .MuiLinearProgress-bar': {
                bgcolor: '#617BFF',
              },
            }}
          />

          {/* Header */}
          <Box
            sx={{
              background: getStepGradient(),
              p: 4,
              textAlign: 'center',
              position: 'relative',
            }}
          >
            {/* Close button */}
            <IconButton
              onClick={handleSkip}
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                color: 'rgba(255,255,255,0.8)',
                '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
              }}
            >
              <CloseIcon />
            </IconButton>

            {/* Step indicator */}
            <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', mb: 2, fontWeight: 500 }}>
              Step {currentStep + 1} of {tutorialSteps.length}
            </Typography>

            {/* Icon */}
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                bgcolor: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
                boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
              }}
            >
              {step.id === 'complete' ? (
                <Box sx={{ 
                  width: 48, 
                  height: 48, 
                  borderRadius: '50%', 
                  bgcolor: '#10B981',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <CheckIcon />
                </Box>
              ) : (
                step.icon
              )}
            </Box>

            <Typography sx={{ fontSize: '22px', fontWeight: 700, color: 'white', mb: 1 }}>
              {step.id === 'welcome' ? `Hey ${firstName}!` : ''} {step.title}
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ p: 4 }}>
            <Typography sx={{ fontSize: '15px', color: '#535862', lineHeight: 1.7, textAlign: 'center', mb: 3 }}>
              {step.description}
            </Typography>

            {/* Workspace input */}
            {step.hasWorkspaceInput && (
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#344054', mb: 1 }}>
                  Workspace Name
                </Typography>
                <TextField
                  fullWidth
                  value={workspaceName}
                  onChange={(e) => setWorkspaceName(e.target.value)}
                  placeholder="Enter workspace name"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                      '& fieldset': { borderColor: '#E5E7EB' },
                      '&:hover fieldset': { borderColor: '#617BFF' },
                      '&.Mui-focused fieldset': { borderColor: '#617BFF' },
                    },
                  }}
                />
              </Box>
            )}

            {/* Code snippet */}
            {step.codeSnippet && (
              <Box
                sx={{
                  bgcolor: '#1E1E1E',
                  borderRadius: '12px',
                  p: 2,
                  mb: 3,
                  overflow: 'auto',
                  maxHeight: 200,
                }}
              >
                <pre style={{ margin: 0, fontSize: '12px', color: '#D4D4D4', fontFamily: 'monospace', lineHeight: 1.5 }}>
                  {step.codeSnippet}
                </pre>
              </Box>
            )}

            {/* Navigation dots */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 3 }}>
              {tutorialSteps.map((_, idx) => (
                <Box
                  key={idx}
                  sx={{
                    width: idx === currentStep ? 24 : 8,
                    height: 8,
                    borderRadius: '4px',
                    bgcolor: idx === currentStep ? '#617BFF' : idx < currentStep ? '#10B981' : '#E5E7EB',
                    transition: 'all 0.3s ease',
                  }}
                />
              ))}
            </Box>

            {/* Navigation buttons */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              {!isFirstStep && (
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  sx={{
                    flex: 1,
                    py: 1.5,
                    borderRadius: '12px',
                    borderColor: '#E5E7EB',
                    color: '#667085',
                    fontWeight: 600,
                    fontSize: '15px',
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: '#D1D5DB',
                      bgcolor: '#F9FAFB',
                    },
                  }}
                >
                  Back
                </Button>
              )}
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={step.hasWorkspaceInput && !workspaceName.trim()}
                sx={{
                  flex: 1,
                  py: 1.5,
                  borderRadius: '12px',
                  bgcolor: isLastStep ? '#10B981' : '#617BFF',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '15px',
                  textTransform: 'none',
                  boxShadow: isLastStep ? '0 4px 14px rgba(16, 185, 129, 0.4)' : '0 4px 14px rgba(97, 123, 255, 0.4)',
                  '&:hover': {
                    bgcolor: isLastStep ? '#059669' : '#5A6FE8',
                  },
                  '&:disabled': {
                    bgcolor: '#E5E7EB',
                    color: '#9CA3AF',
                  },
                }}
              >
                {isLastStep ? 'Start Exploring' : step.hasWorkspaceInput ? 'Create & Continue' : 'Next'}
              </Button>
            </Box>

            {/* Skip link */}
            {!isLastStep && (
              <Button
                variant="text"
                fullWidth
                onClick={handleSkip}
                sx={{
                  mt: 1.5,
                  color: '#9CA3AF',
                  fontSize: '13px',
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: 'transparent',
                    color: '#667085',
                  },
                }}
              >
                Skip tutorial
              </Button>
            )}
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default GatewayTutorial;
